import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import { goToSection, navigate, sectionHref } from '../lib/router'
import { HEADER_NAV, HEADER_ROUTES } from './HeaderNav'
import { SITE } from '../lib/site'

/** E fölött az asztali navigációs sáv látszik, a gomb el van rejtve (CSS). */
const DESKTOP_QUERY = '(min-width: 901px)'

/**
 * MOBIL NAVIGACIO.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT KELL.
 *
 * 900px alatt a fejlec navigacioja `display: none` volt, es nem jott helyette
 * semmi. A latogato - aki a hirdetesbol tulnyomoreszt telefonon erkezik - nem
 * tudott az "Arak" vagy a "GYIK" szekciora ugrani, hanem kb. 12 kepernyot
 * kellett gorgetnie. Egy latogatoi teszt szerint pont ezt a kettot keresi.
 *
 * A telefonszam is ide kerult: eddig csak a lablecben volt elerheto. Nem
 * azert fontos, hogy felhivjak, hanem hogy LASSAK, hogy van kit.
 *
 * A panel modalis (`role="dialog"` + `aria-modal`), mert a nyitott allapotban
 * eltakarja az oldalt - ezert jar hozza fokuszcsapda es gorgetes-zar.
 * ────────────────────────────────────────────────────────────────────
 */
export default function HeaderMobileMenu(): JSX.Element {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const burgerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const close = useCallback((returnFocus = true): void => {
    /*
      A gorgetes-zarat AZONNAL oldjuk fel, nem a React commitjara varva. A
      szekcio-ugras kozvetlenul a zaras utan gorgetne, es amig a
      `body { overflow: hidden }` ervenyben van, a viewport nem gorgetheto -
      az ugras nemán elmaradna. A lenti effekt takaritasa ugyanezt az erteket
      allitja vissza, tehat nem utkoznek.
    */
    document.body.style.overflow = ''
    setOpen(false)
    if (returnFocus) burgerRef.current?.focus()
  }, [])

  /*
    Esc + fokuszcsapda. A csapda azert kell, mert a panel eltakarja az oldalt:
    enelkul a Tab a panel mogotti, nem lathato linkekre vinne a fokuszt.
  */
  useEffect(() => {
    if (!open) return

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      if (!panel) return
      const items = Array.from(
        panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      )
      if (items.length === 0) return

      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  /* Gorgetes-zar, es a fokusz a panel elso elemere. */
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.querySelector<HTMLElement>('a[href]')?.focus()
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  /*
    Ha az ablak asztali meretre no (elforgatott telefon, atmeretezett ablak),
    a panel CSS-ben eltunik - a nyitott allapotot is le kell zarni, kulonben a
    gorgetes-zar bent ragadna egy lathatatlan menu miatt.
  */
  useEffect(() => {
    if (!open) return
    const media = window.matchMedia(DESKTOP_QUERY)
    const onChange = (): void => {
      if (media.matches) close(false)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [open, close])

  const jump = (event: MouseEvent<HTMLAnchorElement>, hash: string): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    close(false)
    goToSection(hash)
  }

  /*
    SAJAT OLDALRA UGRAS. Kulon a `jump`-tol, mert itt NEM gorgetunk egy
    horgonyhoz, hanem utvonalat valtunk - lasd a `HEADER_ROUTES` indoklasat.
    A menut ugyanugy bezarjuk, es a fokuszt NEM adjuk vissza a hamburgernek:
    az uj oldal teteje a helyes kiindulopont.
  */
  const openRoute = (event: MouseEvent<HTMLAnchorElement>, path: string): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    close(false)
    navigate(path)
  }

  return (
    <>
      <button
        ref={burgerRef}
        type="button"
        className="header__burger"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? 'Menü bezárása' : 'Menü megnyitása'}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="header__burger-bars" aria-hidden="true" />
      </button>

      <div
        className="header-menu"
        data-open={open}
        hidden={!open}
        onClick={(event) => {
          if (event.target === event.currentTarget) close()
        }}
      >
        <div
          ref={panelRef}
          id={panelId}
          className="header-menu__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Menü"
        >
          <nav className="header-menu__nav" aria-label="Fő navigáció">
            {HEADER_NAV.map((item, index) => (
              <a
                key={item.hash}
                href={sectionHref(item.hash)}
                onClick={(event) => jump(event, item.hash)}
                /* A lepcsozetes belepteteshez a sor sajat kesleltetest kap. */
                style={{ '--i': index } as CSSProperties}
              >
                <span
                  className={`header__nav-icon header__nav-icon--${item.icon}`}
                  aria-hidden="true"
                >
                  <span className="header__nav-icon-shape" />
                </span>
                <span className="header-menu__label">{item.label}</span>
                <span className="header-menu__chevron" aria-hidden="true" />
              </a>
            ))}

            {/*
              A SAJAT OLDALAK a horgonyok UTAN. Telefonon ez a fontosabb
              hely: aki mar rendelt, azt a "hol tart" erdekli, nem a GYIK.
              A lepcsozetes belepteteshez a sorszamozas FOLYTATODIK, nem
              indul ujra - kulonben ket lista egyszerre kezdene beuszni.
            */}
            {HEADER_ROUTES.map((item, index) => (
              <a
                key={item.id}
                href={item.path}
                onClick={(event) => openRoute(event, item.path)}
                style={{ '--i': HEADER_NAV.length + index } as CSSProperties}
              >
                <span
                  className={`header__nav-icon header__nav-icon--${item.icon}`}
                  aria-hidden="true"
                >
                  <span className="header__nav-icon-shape" />
                </span>
                <span className="header-menu__label">{item.label}</span>
                <span className="header-menu__chevron" aria-hidden="true" />
              </a>
            ))}
          </nav>

          {SITE.phone && (
            <a className="header-menu__phone" href={`tel:${SITE.phone.replace(/\s/g, '')}`}>
              <small>Kérdésed van?</small>
              {SITE.phone}
            </a>
          )}
        </div>
      </div>
    </>
  )
}
